const initialState = {
  byFilterId: {},
  activeUserFields: {},
  activeEventsListId: null
};

// Every map filter holds its own selections, so that the options it's configured with
// only ever apply to the taxonomies and elements that this very filter allows selecting.
// Without this scoping, using a filter configured to only allow one selection at a time
// would reset the selections made in the other filters of the map.
const initialFilterState = {
  activeTaxonomyIds: [],
  activeElementIds: []
};

// A filter always displays the items it's currently showing on the map, so the items it
// starts with are the ones it displays as enabled on load : a filter configured to hide
// its elements until it's used starts with none of them, any other one starts with all
// of them.
export function initialActiveIdsFor(filterElementsOutOnInitialize, selectableIds) {
  return filterElementsOutOnInitialize ? [] : selectableIds;
}

//
// Action types
//

export const INITIALIZE_FILTER_SELECTION = "INITIALIZE_FILTER_SELECTION";

export const SET_TAXONOMY_FILTER_SELECTION = "SET_TAXONOMY_FILTER_SELECTION";

export const UPDATE_ELEMENT_FILTER = "UPDATE_ELEMENT_FILTER";

export const SET_EVENTS_LIST_ACTIVE = "SET_EVENTS_LIST_ACTIVE";

export const UPDATE_USER_FIELD_FILTER = "UPDATE_USER_FIELD_FILTER";

//
// Action creators
//

export function initializeFilterSelection(mapKey, filterId, filterState) {
  return {
    type: INITIALIZE_FILTER_SELECTION,
    mapKey,
    filterId,
    filterState
  };
}

export function setTaxonomyFilterSelection(mapKey, filterId, activeTaxonomyIds) {
  return {
    type: SET_TAXONOMY_FILTER_SELECTION,
    mapKey,
    filterId,
    activeTaxonomyIds
  };
}

export function updateElementFilter(mapKey, filterId, mapElementIds) {
  return {
    type: UPDATE_ELEMENT_FILTER,
    mapKey,
    filterId,
    mapElementIds
  };
}

export function setEventsListActive(mapKey, eventsListId) {
  return {
    type: SET_EVENTS_LIST_ACTIVE,
    mapKey,
    eventsListId
  };
}

export function updateUserFieldFilter(mapKey, userFieldId, value) {
  return {
    type: UPDATE_USER_FIELD_FILTER,
    mapKey,
    userFieldId,
    value
  };
}

//
// Reducer and handlers
//

function filterStateFor(state, filterId) {
  return state.byFilterId[filterId] || initialFilterState;
}

function withFilterState(state, filterId, filterStateChanges) {
  const filterState = { ...filterStateFor(state, filterId), ...filterStateChanges };

  return { ...state, byFilterId: { ...state.byFilterId, [filterId]: filterState } };
}

function initializeFilterSelectionHandler(state, filterId, filterState) {
  // The initial selection is only ever applied once : re-rendering a filter must never
  // discard the selections the visitor has made in it since the map was loaded.
  if (state.byFilterId[filterId]) return state;

  return withFilterState(state, filterId, filterState);
}

// The taxonomies a filter shows are always replaced as a whole : which ones a selection
// excludes within the filter is decided by the filter component, as the single selection
// option only applies to its top-level taxonomies. The selections made in the other
// filters of the map are never touched here.
function setTaxonomyFilterSelectionHandler(state, filterId, activeTaxonomyIds) {
  return withFilterState(state, filterId, { activeTaxonomyIds });
}

function updateElementFilterHandler(state, filterId, mapElementIds) {
  return withFilterState(state, filterId, { activeElementIds: mapElementIds });
}

function setEventsListActiveHandler(state, activeEventsListId) {
  return { ...state, activeEventsListId };
}

function updateUserFieldFilterHandler(state, userFieldId, value) {
  const activeUserFields = { ...state.activeUserFields };

  if (value && value.length) {
    activeUserFields[userFieldId] = value;
  } else {
    delete activeUserFields[userFieldId];
  }

  return { ...state, activeUserFields };
}

export function reduceFilters(state = initialState, action) {
  switch (action && action.type) {
    case INITIALIZE_FILTER_SELECTION:
      return initializeFilterSelectionHandler(state, action.filterId, action.filterState);

    case SET_TAXONOMY_FILTER_SELECTION:
      return setTaxonomyFilterSelectionHandler(
        state,
        action.filterId,
        action.activeTaxonomyIds
      );

    case UPDATE_ELEMENT_FILTER:
      return updateElementFilterHandler(state, action.filterId, action.mapElementIds);

    case SET_EVENTS_LIST_ACTIVE:
      return setEventsListActiveHandler(state, action.eventsListId);

    case UPDATE_USER_FIELD_FILTER:
      return updateUserFieldFilterHandler(state, action.userFieldId, action.value);

    default:
      return state;
  }
}
